/**
 * Full Send Enhanced JavaScript
 * Premium interactions, animations, and functionality
 */

(function() {
  'use strict';

  // ===========================================
  // SCROLL REVEAL ANIMATIONS
  // ===========================================

  const ScrollReveal = {
    observer: null,

    init: function() {
      if (!('IntersectionObserver' in window)) {
        // Fallback: show all elements immediately
        document.querySelectorAll('.fs-reveal').forEach(el => {
          el.classList.add('fs-reveal--visible');
        });
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('fs-reveal--visible');

              // If it's a stagger container, add visible class
              if (entry.target.classList.contains('fs-stagger')) {
                entry.target.classList.add('fs-stagger--visible');
              }

              // Unobserve after revealing
              this.observer.unobserve(entry.target);
            }
          });
        },
        {
          root: null,
          rootMargin: '0px 0px -50px 0px',
          threshold: 0.1
        }
      );

      // Observe all reveal elements
      document.querySelectorAll('.fs-reveal, .fs-stagger').forEach(el => {
        this.observer.observe(el);
      });
    }
  };

  // ===========================================
  // SMOOTH SCROLL
  // ===========================================

  const SmoothScroll = {
    init: function() {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', (e) => {
          const href = anchor.getAttribute('href');
          if (href === '#') return;

          const target = document.querySelector(href);
          if (target) {
            e.preventDefault();
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });

            // Update URL without jumping
            history.pushState(null, null, href);
          }
        });
      });
    }
  };

  // ===========================================
  // TOAST NOTIFICATIONS
  // ===========================================

  const Toast = {
    container: null,

    init: function() {
      // Create container if it doesn't exist
      if (!document.querySelector('.toast-container')) {
        this.container = document.createElement('div');
        this.container.className = 'toast-container';
        this.container.setAttribute('aria-live', 'polite');
        document.body.appendChild(this.container);
      } else {
        this.container = document.querySelector('.toast-container');
      }
    },

    show: function(options) {
      const {
        type = 'info',
        title = '',
        message = '',
        duration = 5000
      } = options;

      const toast = document.createElement('div');
      toast.className = `toast toast--${type}`;
      toast.innerHTML = `
        <div class="toast__icon">
          ${this.getIcon(type)}
        </div>
        <div class="toast__content">
          ${title ? `<div class="toast__title">${title}</div>` : ''}
          ${message ? `<div class="toast__message">${message}</div>` : ''}
        </div>
        <button class="toast__close" aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
          </svg>
        </button>
      `;

      // Add close handler
      toast.querySelector('.toast__close').addEventListener('click', () => {
        this.dismiss(toast);
      });

      this.container.appendChild(toast);

      // Auto dismiss
      if (duration > 0) {
        setTimeout(() => this.dismiss(toast), duration);
      }

      return toast;
    },

    dismiss: function(toast) {
      toast.style.animation = 'toast-out 0.3s ease forwards';
      setTimeout(() => toast.remove(), 300);
    },

    getIcon: function(type) {
      const icons = {
        success: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m22 4-10 10-3-3"/></svg>',
        error: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>',
        warning: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
        info: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>'
      };
      return icons[type] || icons.info;
    },

    success: function(title, message) {
      return this.show({ type: 'success', title, message });
    },

    error: function(title, message) {
      return this.show({ type: 'error', title, message });
    },

    warning: function(title, message) {
      return this.show({ type: 'warning', title, message });
    },

    info: function(title, message) {
      return this.show({ type: 'info', title, message });
    }
  };

  // Add toast dismiss animation
  const toastStyle = document.createElement('style');
  toastStyle.textContent = `
    @keyframes toast-out {
      to {
        opacity: 0;
        transform: translateX(100%);
      }
    }
  `;
  document.head.appendChild(toastStyle);

  // ===========================================
  // ADD TO CART ANIMATION
  // ===========================================

  const AddToCartAnimation = {
    init: function() {
      document.addEventListener('cart:add', (e) => {
        this.animate(e.detail);
      });

      // Intercept add to cart forms — but skip any form that opts out
      // (e.g., the B2B Quick Order list, which has its own bulk submit
      // handler that posts a JSON items array).
      document.querySelectorAll('form[action*="/cart/add"]:not([data-no-default-cart])').forEach(form => {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.handleSubmit(form);
        });
      });
    },

    handleSubmit: async function(form) {
      const submitBtn = form.querySelector('[type="submit"]');
      const originalText = submitBtn.innerHTML;

      // Show loading state
      submitBtn.classList.add('fs-btn--loading');
      submitBtn.disabled = true;

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const item = await response.json();

          // Show success
          submitBtn.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 6 9 17l-5-5"/>
            </svg>
            <span>Added!</span>
          `;
          submitBtn.classList.remove('fs-btn--loading');

          // Dispatch event for cart drawer
          document.dispatchEvent(new CustomEvent('cart:add', { detail: item }));

          // Reset button after delay
          setTimeout(() => {
            submitBtn.innerHTML = originalText;
            submitBtn.disabled = false;
          }, 2000);

        } else {
          throw new Error('Failed to add to cart');
        }

      } catch (error) {
        console.error('Add to cart error:', error);
        submitBtn.classList.remove('fs-btn--loading');
        submitBtn.innerHTML = originalText;
        submitBtn.disabled = false;

        if (window.FullSend && window.FullSend.Toast) {
          window.FullSend.Toast.error('Error', 'Failed to add item to cart');
        }
      }
    },

    animate: function(item) {
      // Update cart count in header
      this.updateCartCount();
    },

    updateCartCount: async function() {
      try {
        const response = await fetch('/cart.js');
        if (response.ok) {
          const cart = await response.json();
          document.querySelectorAll('[data-cart-count]').forEach(el => {
            el.textContent = `(${cart.item_count})`;

            // Add bounce animation
            el.style.animation = 'none';
            el.offsetHeight; // Trigger reflow
            el.style.animation = 'fs-bounce 0.3s ease';
          });
        }
      } catch (error) {
        console.error('Failed to update cart count:', error);
      }
    }
  };

  // Add bounce animation
  const bounceStyle = document.createElement('style');
  bounceStyle.textContent = `
    @keyframes fs-bounce {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.2); }
    }
  `;
  document.head.appendChild(bounceStyle);

  // ===========================================
  // QUANTITY SELECTOR
  // ===========================================

  const QuantitySelector = {
    init: function() {
      document.querySelectorAll('.fs-quantity').forEach(wrapper => {
        const input = wrapper.querySelector('.fs-quantity__input');
        const minusBtn = wrapper.querySelector('[data-qty-minus]');
        const plusBtn = wrapper.querySelector('[data-qty-plus]');

        if (!input) return;

        const min = parseInt(input.min) || 1;
        const max = parseInt(input.max) || 999;

        if (minusBtn) {
          minusBtn.addEventListener('click', () => {
            const current = parseInt(input.value) || min;
            if (current > min) {
              input.value = current - 1;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }

        if (plusBtn) {
          plusBtn.addEventListener('click', () => {
            const current = parseInt(input.value) || min;
            if (current < max) {
              input.value = current + 1;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }

        // Validate on change
        input.addEventListener('change', () => {
          let value = parseInt(input.value) || min;
          value = Math.max(min, Math.min(max, value));
          input.value = value;
        });
      });
    }
  };

  // ===========================================
  // HEADER SCROLL BEHAVIOR
  // Disabled — handled by fs-header-scroll.js
  // ===========================================

  const HeaderScroll = {
    init: function() {}
  };

  // ===========================================
  // VARIANT SELECTOR ENHANCEMENT
  // ===========================================

  const VariantSelector = {
    init: function() {
      // Listen for variant changes
      document.querySelectorAll('[data-variant-select]').forEach(select => {
        select.addEventListener('change', (e) => {
          this.handleChange(e.target);
        });
      });

      // Swatch click handlers
      document.querySelectorAll('[data-variant-swatch]').forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          this.handleSwatchClick(e.target.closest('[data-variant-swatch]'));
        });
      });
    },

    handleChange: function(select) {
      const form = select.closest('form');
      const productId = select.dataset.productId;
      const variantId = select.value;

      this.updatePrice(form, variantId);
      this.updateAvailability(form, variantId);
    },

    handleSwatchClick: function(swatch) {
      const wrapper = swatch.closest('[data-swatch-group]');
      const value = swatch.dataset.value;

      // Update active state
      wrapper.querySelectorAll('[data-variant-swatch]').forEach(s => {
        s.classList.remove('fs-product-card__swatch--active');
      });
      swatch.classList.add('fs-product-card__swatch--active');

      // Update hidden select if exists
      const select = wrapper.querySelector('select');
      if (select) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },

    updatePrice: function(form, variantId) {
      // Price update logic would go here
      // This typically integrates with Shopify's product JSON
    },

    updateAvailability: function(form, variantId) {
      // Availability update logic
    }
  };

  // ===========================================
  // LAZY LOADING ENHANCEMENT
  // ===========================================

  const LazyLoad = {
    init: function() {
      if ('loading' in HTMLImageElement.prototype) {
        // Native lazy loading supported
        document.querySelectorAll('img[loading="lazy"]').forEach(img => {
          img.decoding = 'async';
        });
      } else {
        // Fallback for older browsers
        this.setupIntersectionObserver();
      }
    },

    setupIntersectionObserver: function() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            observer.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px'
      });

      document.querySelectorAll('img[data-src]').forEach(img => {
        observer.observe(img);
      });
    }
  };

  // ===========================================
  // FOCUS TRAP (FOR MODALS/DRAWERS)
  // ===========================================

  const FocusTrap = {
    trap: function(element) {
      const focusableElements = element.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      const handleTab = (e) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
      };

      element.addEventListener('keydown', handleTab);

      return () => {
        element.removeEventListener('keydown', handleTab);
      };
    }
  };

  // ===========================================
  // ACCESSIBILITY ENHANCEMENTS
  // ===========================================

  const Accessibility = {
    init: function() {
      // Skip to main content link
      this.setupSkipLink();

      // Reduce motion preference
      this.handleReducedMotion();

      // Focus visible polyfill
      this.setupFocusVisible();
    },

    setupSkipLink: function() {
      const skipLink = document.querySelector('.skip-to-content');
      if (skipLink) {
        skipLink.addEventListener('click', (e) => {
          const main = document.getElementById('MainContent');
          if (main) {
            e.preventDefault();
            main.setAttribute('tabindex', '-1');
            main.focus();
            main.removeAttribute('tabindex');
          }
        });
      }
    },

    handleReducedMotion: function() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        document.documentElement.classList.add('reduce-motion');
      }
    },

    setupFocusVisible: function() {
      document.body.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          document.body.classList.add('user-is-tabbing');
        }
      });

      document.body.addEventListener('mousedown', () => {
        document.body.classList.remove('user-is-tabbing');
      });
    }
  };

  // ===========================================
  // INITIALIZATION
  // ===========================================

  function init() {
    ScrollReveal.init();
    SmoothScroll.init();
    Toast.init();
    AddToCartAnimation.init();
    QuantitySelector.init();
    HeaderScroll.init();
    VariantSelector.init();
    LazyLoad.init();
    Accessibility.init();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose modules globally
  window.FullSend = window.FullSend || {};
  Object.assign(window.FullSend, {
    ScrollReveal,
    Toast,
    AddToCartAnimation,
    QuantitySelector,
    HeaderScroll,
    VariantSelector,
    FocusTrap,
    Accessibility
  });

})();
